import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Form, formValueSelector, Field as FormField } from 'redux-form';
import Field from './Field';
import CustomQuestions from './CustomQuestions';
import RadioGroup from './CustomQuestions/RadioGroup';
import TextArea from './CustomQuestions/TextArea';
import Checkbox from './Checkbox';
import CategorySelect, { createOption } from './CategorySelect';
import { Button, HeadingFive, Col, TinyText, SmallText } from 'hui/src/styled';
import styled from 'styled-components';
import Loader from '../Loader';
import SuburbSelect from './SuburbSelect';

export const selector = formValueSelector('GQ');
export const CATEGORY_FIELD = 'search_str';

export const START_BY_VALUES = [
  { value: 6, label: 'Emergency' },
  { value: 2, label: 'ASAP, next few days' },
  { value: 8, label: 'Next few weeks' },
  { value: 9, label: "I'm flexible" },
  { value: 10, label: 'Other' }
];

export const StyledHr = styled.hr`
  margin: 30px 0;
  color: #ececec;
  border-width: 0.5px;
  border-style: solid;
  border-color: #ececec;
`;
export const PrimaryLink = styled.a`
  color: ${props => props.theme.primary} !important;
`;

export function GQForm({
  submitting,
  questions,
  handleSubmit,
  selectedSiteIds,
  categories,
  selectedCateogory,
  onSelectCategory,
  change,
  initialCategory,
  buttonText,
  user
}) {
  return (
    <Form onSubmit={handleSubmit}>
      <HeadingFive mb={1}>Job Details</HeadingFive>
      {selectedSiteIds && (
        <SmallText style={{ marginBottom: 35 }}>
          Please answer all questions to let {selectedSiteIds.name} know exactly what you’re looking
          for.
        </SmallText>
      )}
      <Col xs={12} style={{ marginRight: 11 }}>
        <CategorySelect
          label={'What do you need?'}
          initialCategory={initialCategory}
          categories={categories}
          updateField={change}
          selectedCategory={selectedCateogory}
          onSelectCategory={onSelectCategory}
        />
        <SuburbSelect
          label={`Which suburb is the job in?`}
          name="w"
          placeholder="postcode and suburb e.g. 2000 - Sydney"
        />
        <RadioGroup
          options={START_BY_VALUES}
          name="start_by"
          label="When do you need the work to start?"
        />
        <FormField
          label="Job Description"
          name="job_description"
          component={TextArea}
          rows="4"
          placeholder="Please provide as much detail as possible"
        />
      </Col>
      <Col xs={12} style={{ marginRight: 11 }}>
        <CustomQuestions questions={questions} />
      </Col>
      <StyledHr />
      <HeadingFive>My Details</HeadingFive>
      <Col xs={12} style={{ marginTop: 20, marginRight: 11 }}>
        {user.name ? (
          <div>
            <span style={{ fontWeight: 600 }}>Your Name</span> <p>{user.name}</p>
          </div>
        ) : (
          <Field label="Your name" name="full_name" />
        )}
        {user.email ? (
          <div style={{ marginBottom: 25 }}>
            <span style={{ fontWeight: 600 }}>Your Email</span> <p>{user.email}</p>
          </div>
        ) : (
          <Field
            label="Your Email"
            name="email"
            fieldInfoLabel="Why do we need your email address?"
            fieldInfoValue="We need your email address so that we can send you the <br />details of businesses who express interest in your job."
          />
        )}
        <Field
          label="Best number to contact you on"
          name="phone"
          fieldInfoLabel="Why do we need your phone number?"
          fieldInfoValue="We need your phone number so that businesses who <br />express interest in your job may call you for more details."
        />
        {selectedSiteIds && (
          <Checkbox
            label={
              <SmallText style={{ marginBottom: 0, display: 'inline-block' }}>
                Please alert other businesses similar to {selectedSiteIds.name} with my enquiry
              </SmallText>
            }
            name="user_selection_allow_more"
          />
        )}
        <Button xs-block primary disabled={submitting}>
          {submitting ? <Loader color={'#fff'} style={{ minHeight: 20 }} /> : buttonText}
        </Button>
        <TinyText style={{ margin: '10px auto' }}>
          By pressing {buttonText} you agree to the{' '}
          <PrimaryLink target="_blank" rel="noopener" href="/terms">
            Terms and Conditions
          </PrimaryLink>{' '}
          of hipages.
        </TinyText>
      </Col>
    </Form>
  );
}

const TypeCategory = PropTypes.shape({
  seoKey: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.number
});

GQForm.propTypes = {
  submitting: PropTypes.bool,
  questions: PropTypes.array,
  handleSubmit: PropTypes.func,
  categories: PropTypes.arrayOf(TypeCategory),
  selectedCateogory: TypeCategory,
  onSelectCategory: PropTypes.func,
  change: PropTypes.func,
  initialCategory: PropTypes.func,
  initialLocation: PropTypes.func,
  initialStart: PropTypes.string,
  selectedSiteIds: PropTypes.object,
  buttonText: PropTypes.string,
  user: PropTypes.object,
  enableJobsCheckbox: PropTypes.bool
};

export function scrollToFirstError(errors) {
  const [firstField] = Object.keys(errors);

  if (typeof document === 'undefined') {
    return;
  }

  if (firstField === 'search_str') {
    const node = document.querySelector('.Select-control');
    node.scrollIntoView({
      behavior: 'smooth'
    });
    return;
  }
  const node = document.querySelector(`[name="${firstField}"]`);
  node.parentElement.scrollIntoView({
    behavior: 'smooth'
  });
}

const ConnectedForm = reduxForm({
  form: 'GQ',
  initialValues: { user_selection_allow_more: true },
  onSubmitFail: scrollToFirstError
})(GQForm);

const initialValues = (state, props) => {
  const { initialCategory, initialLocation, initialStart, initialAllowMore } = props;
  return {
    user_selection_allow_more: initialAllowMore,
    [CATEGORY_FIELD]: initialCategory ? createOption(initialCategory) : null,
    w: initialLocation,
    start_by: typeof initialStart === 'string' ? initialStart : ''
  };
};

export default connect((state, props) => ({
  selectedCategory: selector(state, CATEGORY_FIELD),
  initialValues: initialValues(state, props)
}))(ConnectedForm);
